import React, { useState } from "react";
import { X, Trash2, ListPlus } from "lucide-react";
import { ICustomField } from "../../models/commissionAndRepayment.model";
import Button, { ButtonType } from "../Assets/Button";
import { usePreventWheelChange } from "../../utils/usePreventWheelChange";

interface RepaymentCustomFieldModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (customFields: ICustomField[]) => void;
  loading?: boolean;
}

const RepaymentCustomFieldModal: React.FC<RepaymentCustomFieldModalProps> = ({
  open,
  onClose,
  onSubmit,
  loading = false,
}) => {
  const getInitialFields = (): ICustomField[] => [
    { customFieldName: "", customFieldValue: 0 },
  ];

  const [customFields, setCustomFields] =
    useState<ICustomField[]>(getInitialFields());

  const handleWheelPrevent = usePreventWheelChange();

  const resetFields = () => {
    setCustomFields(getInitialFields());
  };

  const handleAddField = () => {
    setCustomFields([
      ...customFields,
      { customFieldName: "", customFieldValue: 0 },
    ]);
  };

  const handleRemoveField = (index: number) => {
    if (customFields.length > 1) {
      setCustomFields(customFields.filter((_, i) => i !== index));
    }
  };

  const handleFieldChange = (
    index: number,
    field: keyof ICustomField,
    value: string | number,
  ) => {
    const updatedFields = [...customFields];
    if (field === "customFieldValue") {
      updatedFields[index][field] = Number(value) || 0;
    } else {
      updatedFields[index][field] = value as string;
    }
    setCustomFields(updatedFields);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Filter out empty fields
    const validFields = customFields.filter(
      (field) =>
        field.customFieldName.trim() !== "" && field.customFieldValue > 0,
    );

    if (validFields.length === 0) {
      return;
    }

    onSubmit(validFields);
    resetFields();
  };

  const totalDeduction = customFields.reduce(
    (sum, field) => sum + (Number(field.customFieldValue) || 0),
    0,
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/10 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        <div className="relative bg-white rounded-xl shadow-xl">
          <div className="flex items-center justify-between p-4 border-b border-slate-200">
            <h3 className="text-lg font-semibold text-slate-800">
              Add Custom Deduction
            </h3>
            <button
              onClick={() => {
                resetFields();
                onClose();
              }}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            <div className="space-y-4">
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleAddField}
                  className="flex items-center gap-1 text-xs text-sky-600 hover:text-sky-700 font-medium"
                >
                  <ListPlus className="w-4 h-4" />
                  Add Another Field
                </button>
              </div>
              {customFields.map((field, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg"
                >
                  <div className="flex-1 grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">
                        Field Name
                      </label>
                      <input
                        type="text"
                        value={field.customFieldName}
                        onChange={(e) =>
                          handleFieldChange(
                            index,
                            "customFieldName",
                            e.target.value,
                          )
                        }
                        placeholder="e.g., TDS, Penalty"
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-sm focus:ring-1 focus:ring-navyBlue-primary focus:border-navyBlue-primary outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">
                        Amount (₹)
                      </label>
                      <input
                        type="number"
                        value={field.customFieldValue || ""}
                        onWheel={handleWheelPrevent}
                        onChange={(e) =>
                          handleFieldChange(
                            index,
                            "customFieldValue",
                            e.target.value,
                          )
                        }
                        placeholder="0"
                        min="0"
                        step="0.01"
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-sm focus:ring-1 focus:ring-navyBlue-primary focus:border-navyBlue-primary outline-none"
                        required
                      />
                    </div>
                  </div>
                  {customFields.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveField(index)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}

              {totalDeduction > 0 && (
                <div className="mt-4 px-4 py-2 bg-sky-50 rounded-lg border border-sky-100">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-sky-700">
                      Total Deduction:
                    </span>
                    <span className="text-lg font-semibold text-sky-800">
                      ₹ {totalDeduction.toLocaleString("en-IN")}
                    </span>
                  </div>
                  <p className="text-xs text-red-600">
                    This amount will be deducted from the repayment amount
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-200">
              <Button
                type={ButtonType.Cancel}
                onClick={() => {
                  resetFields();
                  onClose();
                }}
                className="text-xs"
              >
                Cancel
              </Button>
              <Button
                type={ButtonType.Submit}
                disabled={loading}
                onClick={handleSubmit}
                className={`text-xs ${
                  loading
                    ? "bg-sky-400 cursor-not-allowed"
                    : "bg-sky-600 hover:bg-sky-700"
                }`}
              >
                {loading ? "Updating..." : "Update Custom Fields"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RepaymentCustomFieldModal;
